import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Descriptions,
  Avatar,
  Timeline,
  Alert,
  Divider,
  List,
  message,
  Statistic,
  Spin
} from 'antd';
import { 
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  SafetyOutlined,
  TeamOutlined,
  HomeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getAssignment } from '../../api';

const { Title, Text, Paragraph } = Typography;

const AssignmentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assignmentId } = useParams();
  const [assignmentData, setAssignmentData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAssignmentData = async () => {
      // First try to get assignment data from location state
      if (location.state?.assignment) {
        setAssignmentData(location.state.assignment);
        return;
      }

      // If no state data, fetch from API using assignmentId
      if (assignmentId) {
        try {
          setLoading(true);
          const response = await getAssignment(assignmentId);
          
          // Handle different possible response structures
          const assignmentData = response.data || response;
          
          if (assignmentData) {
            setAssignmentData(assignmentData);
          } else {
            throw new Error('No assignment data received');
          }
        } catch (error) {
          console.error('Error fetching assignment data:', error);
          
          // Show specific error message based on error type
          if (error.response?.status === 404) {
            message.error('Assignment not found');
          } else if (error.response?.status === 403) {
            message.error('You do not have permission to view this assignment');
          } else {
            message.error('Failed to load assignment details');
          }
          
          // Redirect back to ongoing assignments on error
          navigate('/agency/ongoing-management');
        } finally {
          setLoading(false);
        }
      } else {
        // No assignment ID available, redirect back
        navigate('/agency/ongoing-management');
      }
    };

    fetchAssignmentData();
  }, [location.state, assignmentId, navigate]);

  if (loading || !assignmentData) {
    return (
      <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Space direction="vertical" size="large">
              {loading && <Spin size="large" />}
              <Text>{loading ? 'Loading assignment details...' : 'No assignment data available'}</Text>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'green',
      'pending': 'orange',
      'completed': 'blue',
      'cancelled': 'red',
      'compliance-in-progress': 'purple'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'active': <CheckCircleOutlined />,
      'pending': <ClockCircleOutlined />,
      'completed': <CheckCircleOutlined />,
      'cancelled': <ExclamationCircleOutlined />,
      'compliance-in-progress': <SafetyOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const handleRefresh = async () => {
    if (assignmentId) {
      try {
        setLoading(true);
        const response = await getAssignment(assignmentId);
        const assignmentData = response.data || response;
        
        if (assignmentData) {
          setAssignmentData(assignmentData);
          message.success('Assignment details refreshed');
        }
      } catch (error) {
        console.error('Error refreshing assignment data:', error);
        message.error('Failed to refresh assignment details');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/agency/ongoing-management')}
            style={{ marginRight: '16px', color: '#006971' }}
          >
            Back to Assignments
          </Button>
          <div>
            <Title level={2} style={{ margin: 0, color: '#006971' }}>
              Assignment Details
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {assignmentData.job?.title || 'Assignment'} • {assignmentData.employee?.name || 'Healthcare Professional'}
            </Text>
          </div>
        </div>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={loading}
            style={{ marginRight: '12px' }}
          >
            Refresh
          </Button>
          <Tag 
            color={getStatusColor(assignmentData.status)} 
            icon={getStatusIcon(assignmentData.status)}
            style={{ fontSize: '14px', padding: '8px 16px', borderRadius: '20px' }}
          >
            {(assignmentData.status || 'pending').toUpperCase()}
          </Tag>
        </Space>
      </div>

      {/* Status Alert */}
      {assignmentData.status === 'active' && (
        <Alert
          message="Assignment Active"
          description="This assignment is currently active. The healthcare professional is working at the assigned facility."
          type="success"
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
          showIcon
        />
      )}
      
      {assignmentData.status === 'pending' && (
        <Alert
          message="Assignment Pending"
          description="This assignment is pending approval or waiting for compliance verification."
          type="warning"
          icon={<ClockCircleOutlined />}
          style={{ marginBottom: '24px', borderRadius: '8px' }}
          showIcon
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Assignment Information */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                Assignment Information
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Job Title" span={2}>
                <Text strong>{assignmentData.job?.title || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Job Code">
                {assignmentData.job?.code || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {assignmentData.job?.department || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {assignmentData.job?.category || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">{assignmentData.job?.type || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                <Space>
                  <CalendarOutlined />
                  {formatDate(assignmentData.startDate)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="End Date">
                <Space>
                  <CalendarOutlined />
                  {formatDate(assignmentData.endDate)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Hours per Day">
                {assignmentData.job?.hoursPerDay || 'N/A'} hours
              </Descriptions.Item>
              <Descriptions.Item label="Days per Week">
                {assignmentData.job?.daysPerWeek || 'N/A'} days
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {assignmentData.duration || 'Ongoing'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Location Information */}
          <Card 
            title={
              <Space>
                <EnvironmentOutlined />
                Facility & Location
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Facility Name">
                <Space>
                  <HomeOutlined />
                  <Text strong>{assignmentData.job?.facilityName || 'Healthcare Facility'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                <div>
                  <div>{assignmentData.job?.address?.street || 'N/A'}</div>
                  <div>
                    {assignmentData.job?.address?.city || 'N/A'}, {assignmentData.job?.address?.state || 'N/A'} {assignmentData.job?.address?.zipCode || ''}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Healthcare Professional & Financial Info */}
        <Col xs={24} lg={8}>
          {/* Healthcare Professional */}
          <Card 
            title={
              <Space>
                <UserOutlined />
                Healthcare Professional
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <Avatar 
                size={80} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#006971', marginBottom: '12px' }}
              />
              <div>
                <Title level={4} style={{ margin: 0, color: '#006971' }}>
                  {assignmentData.employee?.name || 'N/A'}
                </Title>
                <Text type="secondary">{assignmentData.employee?.title || 'Healthcare Professional'}</Text>
              </div>
            </div>
            
            <Divider />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MailOutlined style={{ marginRight: '8px', color: '#666' }} />
                <Text>{assignmentData.employee?.email || 'N/A'}</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <PhoneOutlined style={{ marginRight: '8px', color: '#666' }} />
                <Text>{assignmentData.employee?.phone || 'N/A'}</Text>
              </div>
            </Space>
          </Card>

          {/* Financial Information */}
          <Card 
            title={
              <Space>
                <DollarOutlined />
                Financial Details
              </Space>
            }
            style={{ marginBottom: '24px', borderRadius: '8px' }}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Statistic
                  title="Hourly Rate"
                  value={assignmentData.hourlyRate || assignmentData.job?.salary?.base || 0}
                  prefix="$"
                  suffix="/hr"
                  valueStyle={{ color: '#006971', fontSize: '24px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Hours/Day"
                  value={assignmentData.job?.hoursPerDay || 0}
                  valueStyle={{ fontSize: '18px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Days/Week"
                  value={assignmentData.job?.daysPerWeek || 0}
                  valueStyle={{ fontSize: '18px' }}
                />
              </Col>
            </Row>
          </Card>

          {/* Assignment Timeline */}
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                Timeline
              </Space>
            }
            style={{ borderRadius: '8px' }}
          >
            <Timeline>
              <Timeline.Item color="blue" dot={<CalendarOutlined />}>
                <Text strong>Assignment Created</Text>
                <br />
                <Text type="secondary">
                  {formatDate(assignmentData.createdAt)}
                </Text>
              </Timeline.Item>
              
              {assignmentData.startDate && (
                <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                  <Text strong>Start Date</Text>
                  <br />
                  <Text type="secondary">
                    {formatDate(assignmentData.startDate)}
                  </Text>
                </Timeline.Item>
              )}
              
              {assignmentData.endDate && (
                <Timeline.Item color="orange" dot={<ClockCircleOutlined />}>
                  <Text strong>Expected End Date</Text>
                  <br />
                  <Text type="secondary">
                    {formatDate(assignmentData.endDate)}
                  </Text>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AssignmentDetails; 